import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { useContent, resolveUrl } from "@/lib/content";
import { ImagePickerDialog } from "@/components/live/ImagePickerDialog";
import { Button } from "@/components/ui/button";
import { LayoutGrid, Plus, X, GripVertical } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const KEYS = [1, 2, 3, 4].map((i) => `home.feature.${i}`);

const Slot = ({ id, index, entry, onPick, onClear }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} data-testid={`home-photo-slot-${index + 1}`} className="relative group rounded-lg overflow-hidden bg-sand/30 aspect-[3/4] touch-none">
      {entry?.content ? (
        <>
          <img src={resolveUrl(entry.content)} alt={entry.metadata?.alt || ""} className="w-full h-full object-cover" />
          <button type="button" {...attributes} {...listeners} aria-label="Drag to reorder" data-testid={`home-photo-drag-${index + 1}`}
            className="absolute top-2 left-2 z-10 p-1 rounded bg-white/85 text-charcoal cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100">
            <GripVertical className="w-4 h-4" />
          </button>
          <button type="button" onClick={onClear} aria-label="Clear slot" data-testid={`home-photo-clear-${index + 1}`}
            className="absolute top-2 right-2 z-10 p-1 rounded bg-white/85 text-charcoal opacity-0 group-hover:opacity-100 hover:text-lipstick-red">
            <X className="w-4 h-4" />
          </button>
          <button type="button" onClick={onPick} className="absolute inset-x-2 bottom-2 z-10 py-1.5 rounded bg-charcoal/85 text-ivory text-xs uppercase tracking-wider opacity-0 group-hover:opacity-100" data-testid={`home-photo-change-${index + 1}`}>Change</button>
        </>
      ) : (
        <button type="button" onClick={onPick} data-testid={`home-photo-pick-${index + 1}`} className="w-full h-full flex flex-col items-center justify-center gap-2 text-taupe hover:text-jade-text hover:bg-jade-soft/20 transition-colors">
          <Plus className="w-6 h-6" />
          <span className="font-body text-xs">Slot {index + 1}<br />auto</span>
        </button>
      )}
    </div>
  );
};

export const HomePhotosPicker = () => {
  const { token } = useAuth();
  const { content, refresh } = useContent();
  const [picking, setPicking] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const entries = KEYS.map((k) => content[k]);

  const save = async (sections) => {
    try {
      await axios.put(`${API}/admin/content`, { sections }, { headers: { Authorization: `Bearer ${token}` } });
      await refresh();
      toast.success("Home photos updated");
    } catch (error) {
      toast.error("Failed to update home photos");
    }
  };

  const setSlot = (i, value) => save({ [KEYS[i]]: { content: value?.url || "", metadata: value ? { alt: value.alt || "" } : null } });

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const from = KEYS.indexOf(active.id), to = KEYS.indexOf(over.id);
    const moved = arrayMove(entries, from, to);
    save(Object.fromEntries(KEYS.map((k, i) => [k, { content: moved[i]?.content || "", metadata: moved[i]?.metadata || null }])));
  };

  return (
    <div className="glass rounded-xl p-6 mb-8" data-testid="home-photos-picker">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-heading text-lg text-charcoal flex items-center gap-2"><LayoutGrid className="w-4 h-4 text-jade-text" /> Home Page Photos</h3>
          <p className="font-body text-sm text-stone-grey mt-1">Pick the 4 photos shown in the "Photos Worth a Thousand Words" strip. Empty slots fall back to your latest portraits. Drag to reorder.</p>
        </div>
        {entries.some((e) => e?.content) && (
          <Button type="button" variant="outline" size="sm" data-testid="home-photos-clear-all" onClick={() => save(Object.fromEntries(KEYS.map((k) => [k, { content: "", metadata: null }])))}>Clear all</Button>
        )}
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={KEYS} strategy={horizontalListSortingStrategy}>
          <div className="grid grid-cols-4 gap-3">
            {KEYS.map((k, i) => (
              <Slot key={k} id={k} index={i} entry={entries[i]} onPick={() => setPicking(i)} onClear={() => setSlot(i, null)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <ImagePickerDialog open={picking !== null} onClose={() => setPicking(null)} onSelect={(v) => setSlot(picking, v)} title={`Choose photo for slot ${(picking ?? 0) + 1}`} />
    </div>
  );
};
